import { useEffect, useState } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { QueryBarWebsiteItem, ImageItemContainer } from "@similarweb/ui-components";

import { AutocompleteWebsitesPageMainItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesPageMainItem";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import { queryBarItemTypes } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import { i18nFilter } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { QueryBarItem } from "../QueryBarItem";
import { autocompleteStates } from "../WebsiteQueryBarTypes";

import { LoadingComponent, PageOverlay } from "./WebsiteQueryBar";
import { allTrackers } from "app/services/track/track";

const i18n = i18nFilter();

const QueryBarItemContainer = styled.div`
    margin-left: 16px;
`;

const queryItemImageRenderer = () => {
    return (
        <ImageItemContainer borderColor={colorsPalettes.blue[400]}>
            <SWReactIcons
                iconName="globe"
                size="xs"
                iconColor={colorsPalettes.blue[400]}
            />
        </ImageItemContainer>
    );
};

export const EmptyWebsitePageQueryBar = ({ filterRecentsBy }: { filterRecentsBy: StateOrName }) => {
    const { state } = useCurrentStateAndParams();
    const [recents, setRecents] = useState<any[]>([]);
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            const recentItems = await getAndFilterRecentsAnalysis();
            setRecents(recentItems);
            setIsFetching(false);
        })();
    }, []);

    useEffect(() => {
        // when the page changes open the overlay by default
        setIsOverlayOpen(true);
    }, [state.name]);

    const closeOverlay = () => {
        setIsOverlayOpen(false);
    };

    const getAndFilterRecentsAnalysis = async () => {
        let recentItems;
        try {
            if (filterRecentsBy) {
                recentItems = await getRecentsByParentState(filterRecentsBy, true);
            } else {
                recentItems = await getRecentsAnalysis("website", true);
            }
        } catch (e) {
            Sentry.error(`Error fetching recents - WebsiteQueryBar -`, e);
            return [autocompleteStates.ERROR];
        }
        if (recentItems.length === 0) {
            recentItems[0] = autocompleteStates.NO_DATA_RECENTS;
            return recentItems;
        }

        return recentItems;
    };

    const onItemSelected = (item, type) => {
        const fullUrl = item.fullUrl ?? item.name;
        const urlItems = !item.compareItems
            ? [{ fullUrl, searchType: item.searchType }]
            : [item.mainSite, ...item.compareItems];

        allTrackers.dispatchTrackEvent("Search Bar", "click", `${type || ""}/Empty State/${urlItems.join(",") || ""}`);

        const { keysArr, pagesArr } = urlItems.reduce(
            (agr, urlItem) => {
                const {
                    domain: newDomain,
                    subdomain: newSubdomain,
                    error,
                } = getUrlComponents(urlItem.fullUrl ?? urlItem.domain);
                if (error) {
                    return;
                }

                const page = {
                    url: urlItem.fullUrl ?? urlItem.domain,
                    searchType: urlItem.searchType ?? (newSubdomain ? "subdomain" : "domain"),
                };
                const key = getDomainWithSubdomain(newDomain, newSubdomain);
                if (!agr.keysArr.includes(key)) {
                    agr.keysArr.push(key);
                }
                agr.pagesArr.push(page);
                return agr;
            },
            { keysArr: [], pagesArr: [] },
        );

        const pageFilter = transformSiteListToParam(pagesArr);

        proNavigator.updateQueryParams(
            { key: keysArr.join(","), pageFilter },
            {
                location: "replace",
                reload: false,
            },
        );
    };

    return (
        <>
            <QueryBarItemContainer>
                <QueryBarItem
                    id="FakeWebsiteQueryBar"
                    activeItem={isOverlayOpen ? "FakeWebsiteQueryBar" : ""}
                    isLoading={isFetching}
                    renderComponent={
                        <QueryBarWebsiteItem
                            text={i18n("querybar.missing.param.state.query.placeholder", {
                                itemType: i18n(queryBarItemTypes.website),
                            })}
                            isCompare={false}
                            onItemClick={() => setIsOverlayOpen((state) => !state)}
                            renderImageComponent={queryItemImageRenderer}
                            image=""
                        />
                    }
                    searchComponent={
                        <AutocompleteWebsitesPageMainItem
                            className="AutocompleteWebsitePagesQueryBar"
                            onClick={onItemSelected}
                            onClose={closeOverlay}
                            recentSearches={recents}
                            autocompleteProps={{
                                width: 500,
                            }}
                            showCompareSites={!proNavigator.current().hideCompareButton}
                        />
                    }
                    loadingComponent={
                        <LoadingComponent headerText={i18n("autocomplete.websitesMain.seperator.text.recents")} />
                    }
                />
            </QueryBarItemContainer>
            <div onClick={closeOverlay}>
                <PageOverlay
                    isOpen={isOverlayOpen}
                    hideSidebarOverlay
                />
            </div>
        </>
    );
};
